/** How long the host waits for iframe `ifc:ready` before showing an error. */
export const VIEWER_READY_TIMEOUT_MS = 18000;

/** How long `createIfcViewerSession` may hang before we treat :5080 as down. */
export const VIEWER_SESSION_TIMEOUT_MS = 20000;

/**
 * @param {unknown} err
 * @returns {"no_models" | "no_ready_models" | "viewer_unavailable" | "session_failed" | "generic"}
 */
export function classifySessionHttpError(err) {
  const code = err?.response?.data?.error;
  const status = err?.response?.status;
  if (code === "no_models") return "no_models";
  if (code === "no_ready_models") return "no_ready_models";
  if (code === "viewer_unavailable") return "viewer_unavailable";
  if (err?.code === "session_timeout" || err?.message === "session_timeout") {
    return "viewer_unavailable";
  }
  if (!err?.response) return "viewer_unavailable";
  if (status >= 400 && status < 500) return "session_failed";
  return "generic";
}

/**
 * Fatal `ifc:error` codes from the iframe. Per-model `model_failed` is not
 * fatal — other files may still load and `ifc:ready` still arrives.
 *
 * @param {string | undefined} code
 * @returns {"viewer_down" | "session_failed" | "model_failed" | null}
 */
export function classifyViewerErrorCode(code) {
  if (code === "startup" || code === "viewer_down") return "viewer_down";
  if (code === "session" || code === "manifest") return "session_failed";
  if (code === "all_models_failed") return "model_failed";
  return null;
}

/**
 * @param {{ probedReason?: string, iframeError?: boolean, sawFatalModelError?: boolean }} input
 * @returns {"viewer_down" | "model_failed" | "viewer_timeout"}
 */
export function classifyReadyTimeout({
  probedReason,
  iframeError,
  sawFatalModelError,
} = {}) {
  if (iframeError || probedReason === "unreachable" || probedReason === "missing") {
    return "viewer_down";
  }
  if (sawFatalModelError) return "model_failed";
  return "viewer_timeout";
}

/** @param {string | null} issue */
export function issueMessageKey(issue) {
  switch (issue) {
    case "no_ready_models":
      return "ifc_viewer.error_no_ready_models";
    case "viewer_unavailable":
      return "ifc_viewer.error_viewer_unavailable";
    case "viewer_down":
      return "ifc_viewer.error_viewer_down";
    case "session_failed":
      return "ifc_viewer.error_session";
    case "model_failed":
      return "ifc_viewer.error_model_failed";
    case "viewer_timeout":
      return "ifc_viewer.error_viewer_timeout";
    case "models_failed":
      return "ifc_viewer.error_load";
    default:
      return "ifc_viewer.error_session";
  }
}

/**
 * API `no_models` is only an empty-state when the host also has no rows.
 * If the host already listed models, treat it as a session/filter miss —
 * never the upload card.
 *
 * @param {string} httpIssue
 * @param {{ hostModelCount?: number }} [opts]
 * @returns {string | null}
 */
export function resolveSessionCreateIssue(httpIssue, { hostModelCount = 0 } = {}) {
  if (httpIssue === "no_models") {
    return hostModelCount > 0 ? "no_ready_models" : null;
  }
  return httpIssue;
}

/**
 * Pane precedence. Viewer/session failure always beats the upload empty card.
 *
 * @param {{
 *   loading?: boolean,
 *   creatingSession?: boolean,
 *   sessionIssue?: string | null,
 *   viewerEmbedUrl?: string | null,
 *   modelCount?: number,
 *   viewerHealth?: "unknown" | "ok" | "down",
 * }} input
 * @returns {"issue" | "loading" | "iframe" | "empty"}
 */
export function resolveIfcViewerPane({
  loading = false,
  creatingSession = false,
  sessionIssue = null,
  viewerEmbedUrl = null,
  modelCount = 0,
  viewerHealth = "unknown",
} = {}) {
  if (sessionIssue || viewerHealth === "down") return "issue";
  if (loading || creatingSession) return "loading";
  if (viewerEmbedUrl) return "iframe";
  if (modelCount === 0 && viewerHealth === "unknown") return "loading";
  if (modelCount === 0) return "empty";
  return "loading";
}

export function withTimeout(promise, ms, code = "session_timeout") {
  return new Promise((resolve, reject) => {
    const tid = setTimeout(() => {
      const err = new Error(code);
      err.code = code;
      reject(err);
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(tid);
        resolve(value);
      },
      (err) => {
        clearTimeout(tid);
        reject(err);
      },
    );
  });
}
